import { useSyncExternalStore } from 'react'

/**
 * What a customer answered the push offer, on this device - `FRONTEND.md` §9.737, owner O107.
 *
 * The owner: *"make sure popup for allowing notifications will show always when logged in and they not used
 * "No"*. The banner it replaces remembered its *Teraz nie* in `useState`, so one page view; a reload asked again
 * and **no answer ever stopped it**. Two answers are remembered here, and they last differently on purpose:
 *
 * - ***Nie, ďakujem*** is kept in `localStorage`, for this account on this device, until undone - by switching
 *   push on, from the offer or from the account. **Keyed by the customer's id**, so another account signing in on
 *   the same phone is still asked: a no belongs to the person who said it.
 * - ***Zavrieť*** (the X, Escape, the backdrop, *Rozumiem*) is *not now*: kept in `sessionStorage` with the moment
 *   it was given, and it lasts **for this visit** - this tab, and at most {@link CLOSE_LASTS_MS}. Closing a dialog
 *   is not a no, and reading it as one would be the defect turned round.
 *
 * **Why a close carries a time** (corrected after review). The first draft kept it for the life of the tab, and on a
 * phone a tab lives for days: it is restored with the browser and rarely closed, so one tap on the X silenced the
 * offer as long as a *Nie* would have - the one answer the owner said should stop it. Two hours is long enough for
 * one order to be placed and delivered without being asked twice, and short enough that the next meal is a new
 * visit. A close older than that is no answer; returning to the tab (`visibilitychange`, `focus`, `pageshow`) is
 * when that is noticed, so the offer comes back when the customer does rather than in the middle of a checkout.
 *
 * **A store outside React, read through `useSyncExternalStore`**, because two `usePush` readers are mounted at once
 * - the shell's offer and the account's push row - each with its own React state. A no given in one has to reach
 * the other in the same render, or the offer reopens over the profile the moment the customer switches push off
 * there. Another tab's answer arrives on the browser's own `storage` event.
 *
 * **Another tab switching push on answers the offer in this one** (corrected after review). Switching push on undoes
 * a *Nie* by removing it, and that removal reached this tab as a `storage` event - which woke the offer, with this
 * tab's own copy of the browser still saying push was off, so it opened over a page where push had just been
 * switched on. This tab cannot see the new subscription until it reloads, so for the life of this page that customer
 * counts as answered here; a reload reads the browser and the server afresh.
 *
 * **Storage can refuse.** A locked-down browser throws on `localStorage` and `sessionStorage` alike; then the
 * answer is held in memory for this page instead, so *Nie* still closes the dialog rather than doing nothing - the
 * most a device that will not store anything allows.
 */

const DECLINED_PREFIX = 'fk_push_declined_'
const CLOSED_PREFIX = 'fk_push_closed_'
/** What a *Nie* is stored as; its presence is the answer. `InstallPwaPrompt` writes the same. */
const DECLINED = '1'

/** How long *Zavrieť* lasts at most: one visit, not the life of a tab a phone keeps for days. */
export const CLOSE_LASTS_MS = 2 * 60 * 60 * 1000

type Area = 'local' | 'session'

/** The `localStorage` key that says this customer answered *Nie* on this device. */
export function pushDeclinedKey(customerId: number): string {
  return `${DECLINED_PREFIX}${customerId}`
}

/** The `sessionStorage` key that holds when this customer closed the offer in this tab. */
export function pushClosedKey(customerId: number): string {
  return `${CLOSED_PREFIX}${customerId}`
}

/** Answers the browser refused to store, held for the life of this page instead. */
const unsaved: Record<Area, Map<string, string>> = { local: new Map(), session: new Map() }

/** Customers another tab switched push on for, while this page's copy of the browser cannot see it. */
const answeredElsewhere = new Set<number>()

const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/** The browser's storage for an area, or `null` where there is none to ask. Reading the property can throw. */
function storageFor(area: Area): Storage | null {
  if (typeof window === 'undefined') return null
  return area === 'local' ? window.localStorage : window.sessionStorage
}

function read(area: Area, key: string): string | null {
  const held = unsaved[area].get(key)
  if (held !== undefined) return held
  try {
    return storageFor(area)?.getItem(key) ?? null
  } catch {
    return null
  }
}

function keep(area: Area, key: string, value: string): void {
  try {
    const storage = storageFor(area)
    if (storage === null) unsaved[area].set(key, value)
    else storage.setItem(key, value)
  } catch {
    unsaved[area].set(key, value)
  }
  notify()
}

function forget(area: Area, key: string): void {
  unsaved[area].delete(key)
  try {
    storageFor(area)?.removeItem(key)
  } catch {
    // Nothing the browser would let us store, so nothing it holds to remove.
  }
  notify()
}

/** Whether this customer answered *Nie* on this device. Nobody signed in has answered nothing. */
export function isPushDeclined(customerId: number | null): boolean {
  return customerId !== null && read('local', pushDeclinedKey(customerId)) !== null
}

/**
 * Whether this customer closed the offer for this visit - in this tab, less than {@link CLOSE_LASTS_MS} ago - or
 * another tab switched push on for them while this page was open.
 *
 * A stored value that is not a time - `'1'`, which the first draft wrote - is no answer, so a tab carrying one is
 * asked again. So is a time further in the future than a close lasts, which only a clock moved back can produce.
 */
export function isPushClosedThisTab(customerId: number | null): boolean {
  if (customerId === null) return false
  if (answeredElsewhere.has(customerId)) return true
  const stored = read('session', pushClosedKey(customerId))
  if (stored === null) return false
  const closedAt = Number(stored)
  return Number.isFinite(closedAt) && Math.abs(Date.now() - closedAt) < CLOSE_LASTS_MS
}

/** *Nie, ďakujem* - and switching push off in the account, which says the same (§9.737 point 4). */
export function declinePush(customerId: number | null): void {
  if (customerId === null) return
  keep('local', pushDeclinedKey(customerId), DECLINED)
}

/** Switching push on undoes a *Nie*, from the offer or from the account. */
export function undoPushDecline(customerId: number | null): void {
  if (customerId === null) return
  forget('local', pushDeclinedKey(customerId))
}

/** *Zavrieť* - not again in this tab for this visit. */
export function closePushForThisTab(customerId: number | null): void {
  if (customerId === null) return
  keep('session', pushClosedKey(customerId), String(Date.now()))
}

/** The customer a `fk_push_declined_<id>` key belongs to, or `null` for any other key. */
function declinedCustomerOf(key: string): number | null {
  if (!key.startsWith(DECLINED_PREFIX)) return null
  const id = Number(key.slice(DECLINED_PREFIX.length))
  return Number.isInteger(id) ? id : null
}

/**
 * An answer given in another tab of this device - the browser's `storage` event, which a tab never receives for its
 * own writes.
 */
function onStorage(event: StorageEvent): void {
  // `null` is a `clear()`, which takes every answer with it.
  if (event.key === null) {
    notify()
    return
  }
  const declinedBy = declinedCustomerOf(event.key)
  /* A *Nie* removed by another tab is push switched on there - `undoPushDecline` runs only after a successful
     subscribe. This tab's copy of the browser still says it holds nothing, so the offer would open here over push that
     is on; for this page, that customer has answered. */
  if (declinedBy !== null && event.newValue === null && event.oldValue !== null) {
    answeredElsewhere.add(declinedBy)
  }
  if (declinedBy !== null || event.key.startsWith(CLOSED_PREFIX)) notify()
}

/** The customer has come back to the tab: a close that has outlived its visit is noticed now. */
function onReturn(): void {
  notify()
}

/** The window listeners, installed once for every reader in the tab and removed with the last one. */
let detach: (() => void) | null = null

function listen(): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener('storage', onStorage)
  window.addEventListener('focus', onReturn)
  window.addEventListener('pageshow', onReturn)
  document.addEventListener('visibilitychange', onReturn)
  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('focus', onReturn)
    window.removeEventListener('pageshow', onReturn)
    document.removeEventListener('visibilitychange', onReturn)
  }
}

/**
 * Wakes every reader on an answer given in this tab, on one given in another (`storage`), and when the customer
 * comes back to the tab, which is when a close that has outlived its visit stops counting.
 */
export function subscribePushPromptMemory(onChange: () => void): () => void {
  listeners.add(onChange)
  detach ??= listen()
  return () => {
    listeners.delete(onChange)
    if (listeners.size === 0) {
      detach?.()
      detach = null
      /* Nobody reads the answers any more, and the next reader's copy of the browser is a fresh one
         (`push-browser.ts` forgets its own at the same moment), so what another tab did is no longer hidden from it. */
      answeredElsewhere.clear()
    }
  }
}

/** {@link isPushDeclined}, as a value every reader in the tab agrees on at once. */
export function usePushDeclined(customerId: number | null): boolean {
  return useSyncExternalStore(
    subscribePushPromptMemory,
    () => isPushDeclined(customerId),
    () => false,
  )
}

/** {@link isPushClosedThisTab}, likewise. */
export function usePushClosedThisTab(customerId: number | null): boolean {
  return useSyncExternalStore(
    subscribePushPromptMemory,
    () => isPushClosedThisTab(customerId),
    () => false,
  )
}
